import { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';

// ============================================================
// Report Sign Issue (Aug 14) — the driver's 20-second version.
// Exactly the GroupMe habit, in-system: pick the property, one line
// about the problem, snap photos, send. The Sign Manager fills in the
// full form details before it can be scheduled.
// ============================================================

const MAX_PHOTOS = 6;

export default function SignQuickReportPage() {
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState('');
  const [property, setProperty] = useState(null);
  const [issue, setIssue] = useState('');
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.getSignPropertiesLite().then(setProperties).catch(e => setError(e.message));
  }, []);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return properties
      .filter(p => p.name.toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, properties]);

  function addPhotos(files) {
    const list = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    setPhotos(prev => [...prev, ...list].slice(0, MAX_PHOTOS));
  }

  async function submit() {
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('property_id', property.id);
      fd.append('issue', issue.trim());
      photos.forEach(p => fd.append('photos', p));
      const created = await api.quickSignReport(fd);
      setSent(created);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="admin-page" style={{ maxWidth: 520 }}>
        <div className="card" style={{ padding: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 40 }}>✅</div>
          <h3 style={{ margin: '10px 0 6px' }}>Sent to the sign team</h3>
          <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>
            {sent.account_name || 'Property'} — the sign manager will schedule it.
          </p>
          <button className="btn btn-gold" style={{ fontWeight: 700, marginTop: 18 }}
            onClick={() => { setSent(null); setProperty(null); setSearch(''); setIssue(''); setPhotos([]); }}>
            Report Another
          </button>
        </div>
      </div>
    );
  }

  const ready = property && issue.trim().length >= 5 && photos.length > 0;

  return (
    <div className="admin-page" style={{ maxWidth: 520 }}>
      <div className="page-header">
        <h2>🪧 Report Sign Issue</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Property, what's wrong, photos. Done.
        </p>
      </div>

      {error && <p className="alert alert-error">{error}</p>}

      {/* 1 — property */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>1 · Which property?</div>
        {property ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>{property.name}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{property.address}, {property.city}</div>
            </div>
            <button className="btn btn-muted" style={{ fontSize: 12 }} onClick={() => { setProperty(null); setSearch(''); }}>Change</button>
          </div>
        ) : (
          <>
            <input className="form-input" style={{ width: '100%', padding: '12px 14px', fontSize: 16 }}
              placeholder="Start typing the property name…" value={search} autoFocus
              onChange={e => setSearch(e.target.value)} />
            {matches.length > 0 && (
              <div style={{ marginTop: 6, border: '1px solid var(--border-light, #e2e8f0)', borderRadius: 10, overflow: 'hidden' }}>
                {matches.map(p => (
                  <button key={p.id} onClick={() => setProperty(p)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                      border: 'none', borderBottom: '1px solid var(--border-subtle, #edf2f7)',
                      background: '#fff', cursor: 'pointer', fontSize: 14,
                    }}>
                    <strong>{p.name}</strong>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}> — {p.address}, {p.city}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* 2 — issue */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>2 · What's wrong?</div>
        <textarea className="form-input" rows={2} value={issue}
          placeholder='e.g. "Missing 2 entrance signs" or "Reserved sign knocked down"'
          onChange={e => setIssue(e.target.value)}
          style={{ width: '100%', padding: '12px 14px', fontSize: 16, resize: 'vertical' }} />
      </div>

      {/* 3 — photos */}
      <div className="card" style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>3 · Photos</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {photos.map((p, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={URL.createObjectURL(p)} alt={`upload ${i + 1}`}
                style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border-light, #e2e8f0)' }} />
              <button onClick={() => setPhotos(prev => prev.filter((_, x) => x !== i))}
                style={{
                  position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%',
                  border: 'none', background: '#0f172a', color: '#fff', fontSize: 11, cursor: 'pointer', lineHeight: 1,
                }}>✕</button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <label style={{
              width: 76, height: 76, borderRadius: 10, border: '2px dashed #cbd5e1',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 700,
            }}>
              📸<span>Add</span>
              {/* No `capture` attr — that forces the camera on phones and
                  hides the photo library. Without it iOS/Android offer
                  Take Photo / Photo Library / Files. */}
              <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
            </label>
          )}
        </div>
        {photos.length === 0 && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>At least one photo so the sign team can see it.</div>}
      </div>

      <button className="btn btn-gold" style={{ fontWeight: 800, width: '100%', padding: '14px', fontSize: 16 }}
        disabled={!ready || busy} onClick={submit}>
        {busy ? 'Sending…' : 'Send to Sign Team'}
      </button>
    </div>
  );
}
